(() => {
  'use strict';

  const normalize = (value) => String(value || '').trim().toLowerCase().replace(/&amp;/g, '&').replace(/\s+/g, ' ');
  const key = (value) => normalize(value).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const readJson = (el, fallback) => { try { return JSON.parse(el?.textContent || ''); } catch(e) { return fallback; } };

  const getForm = (el) => el.closest('form[action*="/cart/add"], form[data-type="add-to-cart-form"], form');
  const getQtyInput = (form) => form?.querySelector('input[name="quantity"]');
  const getCurrentOptionValue = (form, optionName, scope = null) => {
    const escapedName = CSS.escape(optionName);
    const roots = [form, scope].filter(Boolean);

    for (const root of roots) {
      const checked = root.querySelector(`input[type="radio"][name="${escapedName}"]:checked`);
      if (checked) return checked.value;

      const select = root.querySelector(`select[name="${escapedName}"]`);
      if (select) return select.value;
    }

    return '';
  };

  const setNativeQuantity = (form, value) => {
    const input = getQtyInput(form);
    if (!input) return;
    const safe = Math.max(0, Number(value) || 0);
    if (String(input.value) === String(safe)) return;
    input.value = safe;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const fireCartEvents = (detail) => {
    ['cart:refresh','cart:open','cart:updated','cart:change','ajaxProduct:added','product:added','xo:cart:refresh','xo:cart:open','xo:cart:add','xo:cart:change'].forEach((name) => {
      document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      window.dispatchEvent(new CustomEvent(name, { detail }));
    });
  };

  const replaceNodeContents = (selector, sourceDoc) => {
    let replaced = false;
    document.querySelectorAll(selector).forEach((node) => {
      const uid = node.getAttribute('xo-unique-id');
      const fresh = sourceDoc.querySelector(uid ? `${selector}[xo-unique-id="${uid}"]` : selector);
      if (!fresh) return;
      node.innerHTML = fresh.innerHTML;
      replaced = true;
    });
    return replaced;
  };

  const refreshCartDrawer = async (detail) => {
    fireCartEvents(detail);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('_camo_multi_flavor_refresh', Date.now().toString());
      const response = await fetch(url.toString(), { credentials: 'same-origin', headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' } });
      if (response.ok) {
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        replaceNodeContents('xo-cart-will-change', doc);
        replaceNodeContents('xo-cart-size', doc);
        replaceNodeContents('[data-cart-count]', doc);
        replaceNodeContents('[data-header-cart-count]', doc);
      }
    } catch(e) {
      console.warn('Camo multi-flavor cart refresh failed', e);
    }
    requestAnimationFrame(() => {
      const trigger = document.querySelector('xo-modal-trigger[xo-name="cart-drawer"], xo-modal-trigger[xo-name="cart"], xo-modal-trigger[xo-name*="cart"], [data-cart-drawer-trigger], [data-cart-trigger], .header__cart, .cart-icon, a[href="/cart"]');
      if (trigger && typeof trigger.click === 'function') trigger.click();
      setTimeout(() => fireCartEvents(detail), 200);
    });
  };

  const initCard = (card) => {
    const form = card.closest('form');
    if (!form || form.dataset.camoMultiFlavorReady === 'true') return;

    const variants = readJson(card.querySelector('[data-enhanced-pricing-variants]'), []);
    const variantOptions = readJson(card.querySelector('[data-enhanced-pricing-variant-options]'), {});
    const productScope = card.closest('.main-product__right, xo-product, .xo-product-info-content') || document;
    if (!Array.isArray(variants) || !variants.length) return;

    const optionNames = Object.values(variantOptions)[0]?.option_names || [];
    const flavorOptionName = optionNames.find((name) => key(name) === 'flavor');
    if (!flavorOptionName) return;

    const flavorWraps = Array.from(productScope.querySelectorAll('[data-camo-flavor-qty-wrap]'));
    if (!flavorWraps.length) return;

    const flavorVariantImages = readJson(productScope.querySelector('[data-camo-flavor-variant-images]'), []);

    form.dataset.camoMultiFlavorReady = 'true';
    // Base quantities represent the customer's one-product flavor mix.
    // The native quantity input becomes the multiplier only for flavor-variant products.
    const state = new Map();
    let syncing = false;
    let multiplier = 0;
    let lastTouchedFlavor = '';
    let flavorVisualStateId = 0;
    let focusStackOnNextVisualState = false;

    const baseTotal = () => Array.from(state.values()).reduce((sum, qty) => sum + Math.max(0, Number(qty) || 0), 0);
    const activeMultiplier = () => baseTotal() > 0 ? Math.max(1, Number(multiplier) || 1) : 0;
    const actualQty = (flavor) => Math.max(0, Number(state.get(flavor)) || 0) * activeMultiplier();
    const actualTotal = () => baseTotal() * activeMultiplier();
    const selectedFlavor = () => getCurrentOptionValue(form, flavorOptionName, productScope) || flavorWraps[0]?.dataset.camoFlavor || '';

    const updateFlavorVariantImages = () => {
      if (!Array.isArray(flavorVariantImages) || !flavorVariantImages.length) return;

      const flavorIndex = optionNames.findIndex((name) => key(name) === 'flavor');
      if (flavorIndex < 0) return;

      // XO synchronizes the exact selected Type / Pack Type / Flavor combination
      // into the product form's hidden variant ID. Use that variant's option array
      // as the source of truth instead of querying visible radio groups, because the
      // product UI may contain duplicate option controls whose checked state is stale.
      const nativeVariantId = String(form.querySelector('input[name="id"], select[name="id"]')?.value || '');
      const nativeEntry = flavorVariantImages.find((entry) => String(entry.id) === nativeVariantId);
      const selectedOptions = Array.isArray(nativeEntry?.options)
        ? [...nativeEntry.options]
        : optionNames.map((name) => getCurrentOptionValue(form, name, productScope));

      flavorWraps.forEach((wrap) => {
        const flavor = wrap.dataset.camoFlavor || '';
        const match = flavorVariantImages.find((entry) => {
          if (!Array.isArray(entry.options)) return false;
          return optionNames.every((name, index) => {
            const desired = index === flavorIndex ? flavor : selectedOptions[index];
            return !desired || normalize(entry.options[index]) === normalize(desired);
          });
        });
        if (!match?.image) return;

        const artwork = wrap.querySelector('.camo-flavor-gallery__artwork');
        if (!artwork) return;

        let image = artwork.querySelector('.camo-flavor-gallery__image');
        if (!image) {
          image = document.createElement('img');
          image.className = 'camo-flavor-gallery__image';
          image.loading = 'lazy';
          image.alt = '';
          artwork.prepend(image);
        }

        if (image.getAttribute('src') === match.image && !image.hasAttribute('srcset')) return;
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.setAttribute('src', match.image);
      });
    };

    let flavorImageUpdateFrame = 0;
    const scheduleFlavorVariantImageUpdate = () => {
      if (flavorImageUpdateFrame) cancelAnimationFrame(flavorImageUpdateFrame);
      flavorImageUpdateFrame = requestAnimationFrame(() => {
        flavorImageUpdateFrame = requestAnimationFrame(() => {
          flavorImageUpdateFrame = 0;
          updateFlavorVariantImages();
        });
      });
    };

    const scheduleSettledFlavorVariantImageUpdate = () => {
      scheduleFlavorVariantImageUpdate();
      [50, 150, 300].forEach((delay) => window.setTimeout(scheduleFlavorVariantImageUpdate, delay));
    };

    const announceActualQuantity = () => {
      form.dataset.camoMultiFlavorBaseQuantity = String(baseTotal());
      form.dataset.camoMultiFlavorMultiplier = String(activeMultiplier());
      form.dataset.camoMultiFlavorActualQuantity = String(actualTotal());
      form.dispatchEvent(new CustomEvent('camo:multi-flavor-quantity-change', {
        bubbles: true,
        detail: {
          baseQuantity: baseTotal(),
          multiplier: activeMultiplier(),
          actualQuantity: actualTotal()
        }
      }));
    };

    const emitFlavorVisualState = () => {
      window.clearTimeout(flavorVisualStateId);
      flavorVisualStateId = window.setTimeout(() => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const items = [];
          state.forEach((qty, flavor) => {
            const variantId = findVariantId(flavor);
            const finalQty = Math.max(0, Number(qty) || 0) * activeMultiplier();
            if (variantId && finalQty > 0) items.push({ variantId: Number(variantId), flavor, qty: finalQty });
          });
          const activeFlavor = lastTouchedFlavor || selectedFlavor() || Array.from(state.keys()).slice(-1)[0] || '';
          const activeVariantId = activeFlavor ? findVariantId(activeFlavor) : null;
          const event = new CustomEvent('camo:product-flavor-visual-state', {
            bubbles: true,
            detail: {
              items,
              activeVariantId: activeVariantId ? Number(activeVariantId) : null,
              focusStack: focusStackOnNextVisualState
            }
          });
          focusStackOnNextVisualState = false;
          (productScope.closest('xo-product') || productScope).dispatchEvent(event);
        }));
      }, 250);
    };

    const render = () => {
      multiplier = baseTotal() > 0 ? Math.max(1, Number(multiplier) || 1) : 0;
      flavorWraps.forEach((wrap) => {
        const flavor = wrap.dataset.camoFlavor || '';
        const qty = actualQty(flavor);
        const badge = wrap.querySelector('[data-camo-flavor-badge]');
        wrap.classList.toggle('has-camo-flavor-qty', qty > 0);
        if (badge) {
          badge.textContent = qty;
          badge.hidden = qty <= 0;
        }
      });
      syncing = true;
      setNativeQuantity(form, activeMultiplier());
      syncing = false;
      announceActualQuantity();
      emitFlavorVisualState();
    };

    const changeFlavor = (flavor, delta) => {
      if (!flavor) return;
      const current = Math.max(0, Number(state.get(flavor)) || 0);
      const next = Math.max(0, current + delta);
      if (next > 0) {
        state.set(flavor, next);
        lastTouchedFlavor = flavor;
      } else {
        state.delete(flavor);
        if (lastTouchedFlavor === flavor) lastTouchedFlavor = selectedFlavor() || Array.from(state.keys()).slice(-1)[0] || '';
      }
      focusStackOnNextVisualState = true;
      render();
    };


    const addOneToBalancedFlavor = (flavors) => {
      const candidates = flavors.filter(Boolean);
      if (!candidates.length) return;
      const minQty = Math.min(...candidates.map((flavor) => Math.max(0, Number(state.get(flavor)) || 0)));
      const lowest = candidates.filter((flavor) => Math.max(0, Number(state.get(flavor)) || 0) === minQty);
      const chosen = lowest.includes(lastTouchedFlavor) ? lastTouchedFlavor : lowest[0];
      if (!chosen) return;
      state.set(chosen, Math.max(0, Number(state.get(chosen)) || 0) + 1);
      lastTouchedFlavor = chosen;
    };

    const applyMilestoneQuantity = (targetQuantity) => {
      const target = Math.max(0, parseInt(targetQuantity, 10) || 0);
      if (target <= 0) return;

      // Milestones only top up the flavor button quantities on flavor-variant products.
      // They do not set or bump the main xo-cart-quantity multiplier.
      if (baseTotal() <= 0) {
        const firstFlavor = selectedFlavor() || flavorWraps[0]?.dataset.camoFlavor || '';
        if (firstFlavor) {
          state.set(firstFlavor, 0);
          lastTouchedFlavor = firstFlavor;
        }
      }

      let guard = 0;
      while (actualTotal() < target && guard < 500) {
        const activeFlavors = Array.from(state.keys()).filter((flavor) => Math.max(0, Number(state.get(flavor)) || 0) > 0);
        const flavors = activeFlavors.length ? activeFlavors : [lastTouchedFlavor || selectedFlavor() || flavorWraps[0]?.dataset.camoFlavor || ''];
        addOneToBalancedFlavor(flavors);
        guard += 1;
      }

      focusStackOnNextVisualState = true;
      render();
    };

    form.camoApplyFlavorMilestone = applyMilestoneQuantity;

    const findVariantId = (flavor) => {
      // Shopify/XO already keeps the product form's hidden variant ID synchronized
      // with the visible Type and Pack Type controls. Use that native selected variant
      // as the source of truth, then replace only its Flavor option for each cart line.
      // This avoids reading an inactive duplicate radio group elsewhere in the product UI.
      const nativeVariantId = Number(form.querySelector('input[name="id"], select[name="id"]')?.value || 0);
      const nativeVariant = variants.find((variant) => Number(variant.id) === nativeVariantId);
      const flavorIndex = optionNames.findIndex((name) => key(name) === 'flavor');

      if (nativeVariant && flavorIndex >= 0) {
        const match = variants.find((variant) => {
          return optionNames.every((name, index) => {
            const desired = index === flavorIndex
              ? flavor
              : nativeVariant[`option${index + 1}`];
            return normalize(variant[`option${index + 1}`]) === normalize(desired);
          });
        });
        if (match) return match.id;
      }

      // Narrow fallback for themes that have not synchronized the hidden variant input yet.
      const selected = {};
      optionNames.forEach((name) => { selected[key(name)] = getCurrentOptionValue(form, name, productScope); });
      selected.flavor = flavor;

      const fallbackMatch = variants.find((variant) => {
        return optionNames.every((name, index) => {
          const optionKey = key(name);
          const desired = selected[optionKey];
          if (!desired) return true;
          return normalize(variant[`option${index + 1}`]) === normalize(desired);
        });
      });
      return fallbackMatch?.id || null;
    };

    let lastFlavorClick = { flavor: '', time: 0 };

    productScope.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (!optionNames.some((name) => normalize(name) === normalize(target.name))) return;
      if (key(target.name) === 'flavor') return;
      scheduleSettledFlavorVariantImageUpdate();
    });

    ['variant:change', 'product:variant-change', 'theme:variant:change', 'xo:variant:change'].forEach((eventName) => {
      productScope.addEventListener(eventName, scheduleSettledFlavorVariantImageUpdate);
    });

    const nativeVariantInput = form.querySelector('input[name="id"], select[name="id"]');
    if (nativeVariantInput) {
      new MutationObserver(scheduleSettledFlavorVariantImageUpdate).observe(nativeVariantInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    }

    scheduleSettledFlavorVariantImageUpdate();

    flavorWraps.forEach((wrap) => {
      wrap.addEventListener('click', (event) => {
        const flavor = wrap.dataset.camoFlavor || '';
        if (event.target.closest('[data-camo-flavor-minus]')) {
          event.preventDefault(); event.stopPropagation();
          changeFlavor(flavor, -1);
          return;
        }
        if (event.target.closest('[data-camo-flavor-plus]')) {
          event.preventDefault(); event.stopPropagation();
          changeFlavor(flavor, 1);
          return;
        }
        // Let the native radio selection happen first, then count the flavor click as +1.
        // Some label/input structures emit two click events; keep the flavor count to one.
        const now = Date.now();
        if (lastFlavorClick.flavor === flavor && now - lastFlavorClick.time < 160) return;
        lastFlavorClick = { flavor, time: now };
        window.setTimeout(() => changeFlavor(flavor, 1), 0);
      });
    });

    const gallery = productScope.querySelector('.xo-product-info-variant__inner--camo-flavor');
    if (gallery && gallery.dataset.camoFlavorGalleryReady !== 'true') {
      gallery.dataset.camoFlavorGalleryReady = 'true';
      gallery.classList.add('camo-flavor-gallery');
      gallery.setAttribute('tabindex', gallery.getAttribute('tabindex') || '0');

      const galleryRoot = gallery.closest('.xo-product-info-variant--camo-flavor') || gallery.parentElement;
      const logicalWraps = [...flavorWraps];
      let galleryFrame = 0;
      let galleryScrolling = false;
      let loopJumping = false;
      let previousScrollLeft = gallery.scrollLeft;
      let viewMode = logicalWraps.length >= 7 ? 'grid' : 'carousel';

      const pagination = document.createElement('div');
      pagination.className = 'camo-flavor-gallery__pagination';
      pagination.setAttribute('role', 'tablist');
      pagination.setAttribute('aria-label', 'Flavor gallery');

      const dots = logicalWraps.map((wrap, index) => {
        const dot = document.createElement('button');
        const flavor = wrap.dataset.camoFlavor || `Flavor ${index + 1}`;
        dot.type = 'button';
        dot.className = 'camo-flavor-gallery__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', flavor);
        dot.setAttribute('aria-selected', 'false');
        dot.addEventListener('click', () => {
          if (viewMode !== 'carousel') return;
          setGalleryItemState(wrap, true);
          wrap.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
        pagination.appendChild(dot);
        return dot;
      });
      galleryRoot?.appendChild(pagination);

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'camo-flavor-view-toggle';
      toggle.dataset.camoFlavorViewToggle = '';
      toggle.setAttribute('aria-pressed', 'false');
      toggle.innerHTML = '<span class="camo-flavor-view-toggle__icon" aria-hidden="true"></span>';
      galleryRoot?.appendChild(toggle);

      const setGalleryItemState = (activeWrap, selectFlavor = false) => {
        if (!activeWrap) return;
        logicalWraps.forEach((item, index) => {
          const isActive = item === activeWrap;
          item.classList.toggle('is-camo-flavor-gallery-active', isActive);
          item.setAttribute('aria-current', isActive ? 'true' : 'false');
          dots[index]?.classList.toggle('is-active', isActive);
          dots[index]?.setAttribute('aria-selected', isActive ? 'true' : 'false');
          if (isActive) {
            const accent = getComputedStyle(item).getPropertyValue('--camo-flavor-accent-resolved').trim();
            if (accent) dots[index]?.style.setProperty('--camo-dot-accent', accent);
          }
        });

        if (selectFlavor) {
          const input = activeWrap.querySelector('input[type="radio"]');
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('input', { bubbles: true }));
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      };

      const activeItem = () => logicalWraps.find((item) => item.classList.contains('is-camo-flavor-gallery-active'))
        || logicalWraps.find((item) => item.querySelector('input[type="radio"]:checked'))
        || logicalWraps[0];

      const restoreLogicalOrder = () => logicalWraps.forEach((item) => gallery.appendChild(item));

      const centerCarouselItem = (item, behavior = 'auto') => {
        if (!item) return;
        restoreLogicalOrder();
        if (logicalWraps.length > 2) {
          const activeIndex = logicalWraps.indexOf(item);
          const desiredIndex = Math.floor(logicalWraps.length / 2);
          const split = (activeIndex - desiredIndex + logicalWraps.length) % logicalWraps.length;
          logicalWraps.slice(split).concat(logicalWraps.slice(0, split)).forEach((entry) => gallery.appendChild(entry));
        }
        item.scrollIntoView({ behavior, block: 'nearest', inline: 'center' });
        previousScrollLeft = gallery.scrollLeft;
      };

      const updateToggle = () => {
        const gridActive = viewMode === 'grid';
        galleryRoot?.classList.toggle('is-camo-flavor-grid-view', gridActive);
        galleryRoot?.classList.toggle('is-camo-flavor-carousel-view', !gridActive);
        gallery.dataset.camoFlavorView = viewMode;
        toggle.dataset.camoFlavorView = viewMode;
        toggle.setAttribute('aria-pressed', gridActive ? 'true' : 'false');
        toggle.setAttribute('aria-label', gridActive ? 'Show flavor carousel' : 'Show flavor grid');
        toggle.setAttribute('title', gridActive ? 'Show flavor carousel' : 'Show flavor grid');
      };

      const applyView = (nextMode, initial = false) => {
        const currentActive = activeItem();
        viewMode = nextMode === 'grid' ? 'grid' : 'carousel';
        updateToggle();
        window.clearTimeout(gallery._camoFlavorScrollEnd);
        galleryScrolling = false;
        if (galleryFrame) cancelAnimationFrame(galleryFrame);
        galleryFrame = 0;

        if (viewMode === 'grid') {
          restoreLogicalOrder();
          gallery.scrollLeft = 0;
          requestAnimationFrame(() => currentActive?.scrollIntoView({ behavior: initial ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' }));
        } else {
          gallery.scrollTop = 0;
          requestAnimationFrame(() => centerCarouselItem(currentActive, initial ? 'auto' : 'smooth'));
        }
        setGalleryItemState(currentActive, false);
      };

      toggle.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        applyView(viewMode === 'grid' ? 'carousel' : 'grid');
      });

      const itemStep = () => {
        const items = [...gallery.querySelectorAll('[data-camo-flavor-gallery-item]')];
        if (items.length < 2) return items[0]?.getBoundingClientRect().width || 0;
        return Math.abs(items[1].offsetLeft - items[0].offsetLeft) || items[0].getBoundingClientRect().width;
      };

      const jumpWithoutAnimation = (callback) => {
        loopJumping = true;
        gallery.classList.add('is-camo-loop-jumping');
        callback();
        gallery.getBoundingClientRect();
        requestAnimationFrame(() => {
          gallery.classList.remove('is-camo-loop-jumping');
          loopJumping = false;
        });
      };

      const rebalanceInfiniteLoop = () => {
        if (viewMode !== 'carousel' || logicalWraps.length < 3 || loopJumping) return;
        const step = itemStep();
        if (!step) return;
        const max = gallery.scrollWidth - gallery.clientWidth;
        const movingRight = gallery.scrollLeft >= previousScrollLeft;

        if (movingRight && gallery.scrollLeft > max - step * 1.35) {
          const first = gallery.querySelector('[data-camo-flavor-gallery-item]');
          if (first) jumpWithoutAnimation(() => {
            gallery.appendChild(first);
            gallery.scrollLeft -= step;
          });
        } else if (!movingRight && gallery.scrollLeft < step * 1.35) {
          const items = gallery.querySelectorAll('[data-camo-flavor-gallery-item]');
          const last = items[items.length - 1];
          if (last) jumpWithoutAnimation(() => {
            gallery.insertBefore(last, items[0]);
            gallery.scrollLeft += step;
          });
        }
        previousScrollLeft = gallery.scrollLeft;
      };

      const closestToGalleryCenter = () => {
        const galleryRect = gallery.getBoundingClientRect();
        const center = galleryRect.left + galleryRect.width / 2;
        return logicalWraps.reduce((closest, item) => {
          const rect = item.getBoundingClientRect();
          const distance = Math.abs((rect.left + rect.width / 2) - center);
          return !closest || distance < closest.distance ? { item, distance } : closest;
        }, null)?.item;
      };

      const updateGalleryFromScroll = () => {
        galleryFrame = 0;
        if (viewMode !== 'carousel') return;
        rebalanceInfiniteLoop();
        const activeWrap = closestToGalleryCenter();
        setGalleryItemState(activeWrap, galleryScrolling && !loopJumping);
      };

      const queueGalleryUpdate = () => {
        if (viewMode !== 'carousel' || loopJumping) return;
        galleryScrolling = true;
        if (!galleryFrame) galleryFrame = requestAnimationFrame(updateGalleryFromScroll);
        window.clearTimeout(gallery._camoFlavorScrollEnd);
        gallery._camoFlavorScrollEnd = window.setTimeout(() => {
          galleryScrolling = false;
          rebalanceInfiniteLoop();
          const activeWrap = closestToGalleryCenter();
          setGalleryItemState(activeWrap, true);
          activeWrap?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 180);
      };

      gallery.addEventListener('scroll', queueGalleryUpdate, { passive: true });
      gallery.addEventListener('keydown', (event) => {
        const arrows = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (!arrows.includes(event.key)) return;

        if (viewMode === 'grid') {
          const current = activeItem();
          const currentIndex = Math.max(0, logicalWraps.indexOf(current));
          const movement = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -5, ArrowDown: 5 }[event.key];
          const nextIndex = Math.min(logicalWraps.length - 1, Math.max(0, currentIndex + movement));
          if (nextIndex === currentIndex) return;
          event.preventDefault();
          const next = logicalWraps[nextIndex];
          setGalleryItemState(next, true);
          next?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
          next?.querySelector('input[type="radio"]')?.focus({ preventScroll: true });
          return;
        }

        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const domWraps = [...gallery.querySelectorAll('[data-camo-flavor-gallery-item]')];
        const activeIndex = Math.max(0, domWraps.findIndex((item) => item.classList.contains('is-camo-flavor-gallery-active')));
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (activeIndex + direction + domWraps.length) % domWraps.length;
        event.preventDefault();
        domWraps[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      });

      logicalWraps.forEach((wrap) => {
        wrap.addEventListener('click', () => {
          setGalleryItemState(wrap, false);
          wrap.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: viewMode === 'carousel' ? 'center' : 'nearest'
          });
        });
      });

      const initiallyChecked = logicalWraps.find((wrap) => wrap.querySelector('input[type="radio"]:checked')) || logicalWraps[0];
      setGalleryItemState(initiallyChecked, false);
      requestAnimationFrame(() => applyView(viewMode, true));
    }

    const qtyInput = getQtyInput(form);
    if (qtyInput) {
      qtyInput.min = '0';
      qtyInput.value = '0';
    }
    const quantityControl = qtyInput?.closest('xo-cart-quantity, .xo-quantity-style2');
    if (quantityControl) {
      quantityControl.classList.add('camo-flavor-main-quantity-multiplier');
      if (!quantityControl.querySelector('.camo-flavor-main-qty-prefix')) {
        const prefix = document.createElement('span');
        prefix.className = 'camo-flavor-main-qty-prefix';
        prefix.setAttribute('aria-hidden', 'true');
        prefix.textContent = '×';
        quantityControl.insertBefore(prefix, qtyInput);
      }
    }

    const clearSelections = () => {
      state.clear();
      multiplier = 0;
      focusStackOnNextVisualState = true;
      render();
    };

    const setMultiplier = (value) => {
      const number = Number(value);
      if (baseTotal() > 0 && number <= 0) {
        clearSelections();
        return;
      }
      multiplier = baseTotal() > 0 ? Math.max(1, number || 1) : Math.max(0, number || 0);
      focusStackOnNextVisualState = true;
      render();
    };

    quantityControl?.addEventListener('click', (event) => {
      if (syncing) return;
      const button = event.target.closest('xo-cart-quantity-minus, xo-cart-quantity-plus, .xo-quantity-style2__button');
      const buttons = Array.from(quantityControl.querySelectorAll('xo-cart-quantity-minus, xo-cart-quantity-plus, .xo-quantity-style2__button'));
      const minus = button?.matches('xo-cart-quantity-minus') || button === buttons[0];
      const plus = button?.matches('xo-cart-quantity-plus') || button === buttons[buttons.length - 1];
      if (!minus && !plus) return;

      // On flavor-variant products, the main purchase-card quantity is a multiplier.
      // It must not add/subtract flavor button counts; it only scales every selected flavor.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      setMultiplier(activeMultiplier() + (plus ? 1 : -1));
    }, true);

    qtyInput?.addEventListener('input', () => {
      if (syncing) return;
      setMultiplier(qtyInput.value);
    });

    qtyInput?.addEventListener('change', () => {
      if (syncing) return;
      setMultiplier(qtyInput.value);
    });

    const buildItems = () => {
      const items = [];
      state.forEach((qty, flavor) => {
        const id = findVariantId(flavor);
        const finalQty = Math.max(0, Number(qty) || 0) * activeMultiplier();
        if (id && finalQty > 0) items.push({ id: Number(id), quantity: Number(finalQty) });
      });
      return items;
    };

    const addItems = async (checkout) => {
      const items = buildItems();
      if (!items.length) return false;
      const addButton = card.querySelector('xo-cart-add');
      addButton?.setAttribute('xo-loading', 'true');
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items })
        });
        const resultText = await response.text();
        let result = {}; try { result = resultText ? JSON.parse(resultText) : {}; } catch(e) { result = { raw: resultText }; }
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add selected flavors to cart.');
        const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.ok ? r.json() : null).catch(() => null);
        const detail = { items, response: result, cart };
        if (checkout) { window.location.href = '/checkout'; return true; }
        await refreshCartDrawer(detail);
        return true;
      } catch(e) {
        console.error(e);
        window.alert(e && e.message ? e.message : 'Unable to add selected flavors to cart.');
        return true;
      } finally {
        addButton?.removeAttribute('xo-loading');
      }
    };

    form.addEventListener('submit', async (event) => {
      if (baseTotal() <= 0) return;
      event.preventDefault(); event.stopPropagation();
      await addItems(false);
    }, true);

    card.querySelector('.buy-button__add-wrap')?.addEventListener('click', async (event) => {
      if (baseTotal() <= 0) return;
      event.preventDefault(); event.stopPropagation();
      await addItems(false);
    }, true);

    card.querySelector('.buy-button__now-wrap')?.addEventListener('click', async (event) => {
      if (baseTotal() <= 0) return;
      event.preventDefault(); event.stopPropagation();
      await addItems(true);
    }, true);

    render();
  };

  const init = () => document.querySelectorAll('[data-enhanced-pricing-card]').forEach(initCard);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
